import React, { useState, useEffect } from 'react';
import { Brain, Target, Award, CheckCircle, Lock, ArrowRight, Play, BookOpen } from 'lucide-react';
import ReactPlayer from 'react-player';

type Module = {
  id: string;
  title: string;
  description: string;
  type: 'video' | 'interactive' | 'challenge' | 'assessment';
  duration: string;
  xp: number;
  status: 'locked' | 'available' | 'in-progress' | 'completed';
  videoUrl?: string;
  content?: string;
};

type Path = {
  id: string;
  title: string;
  description: string;
  level: 'principiante' | 'intermedio' | 'avanzado';
  progress: number;
  modules: Module[];
};

type AILearningPathProps = {
  userId: string;
  careerPath: string;
};

const AILearningPath: React.FC<AILearningPathProps> = ({ userId, careerPath }) => {
  const [currentPath, setCurrentPath] = useState<Path | null>(null);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate API call to get personalized learning path
    const fetchLearningPath = async () => {
      // In a real app, this would be an API call
      const mockPath: Path = {
        id: 'path-1',
        title: 'Desarrollo de Producto Digital',
        description: 'Aprende a crear productos digitales exitosos desde cero',
        level: 'intermedio',
        progress: 35,
        modules: [
          {
            id: 'mod-1',
            title: 'Fundamentos de Product Management',
            description: 'Conceptos básicos y metodologías esenciales',
            type: 'video',
            duration: '15 min',
            xp: 100,
            status: 'completed',
            videoUrl: 'https://www.youtube.com/watch?v=example'
          },
          {
            id: 'mod-2',
            title: 'Investigación de Usuario',
            description: 'Técnicas efectivas de investigación de usuarios',
            type: 'interactive',
            duration: '30 min',
            xp: 150,
            status: 'in-progress',
            content: 'Contenido interactivo sobre investigación de usuarios...'
          },
          {
            id: 'mod-3',
            title: 'Definición de Producto',
            description: 'Cómo definir un producto exitoso',
            type: 'challenge',
            duration: '45 min',
            xp: 200,
            status: 'available'
          },
          {
            id: 'mod-4',
            title: 'Priorización y Roadmap',
            description: 'Estrategias de priorización y creación de roadmaps',
            type: 'assessment',
            duration: '1h',
            xp: 250,
            status: 'locked'
          }
        ]
      };

      setCurrentPath(mockPath);
      setLoading(false);
    };

    fetchLearningPath();
  }, [userId, careerPath]);

  const getModuleIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Play className="h-5 w-5 text-blue-500" />;
      case 'interactive':
        return <Brain className="h-5 w-5 text-purple-500" />;
      case 'challenge':
        return <Target className="h-5 w-5 text-orange-500" />;
      case 'assessment':
        return <BookOpen className="h-5 w-5 text-green-500" />;
      default:
        return <Brain className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'locked':
        return <Lock className="h-5 w-5 text-gray-400" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!currentPath) {
    return (
      <div className="text-center text-gray-600">
        No se encontró una ruta de aprendizaje.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{currentPath.title}</h2>
            <p className="mt-1 text-gray-600">{currentPath.description}</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-500">
              Nivel: {currentPath.level.charAt(0).toUpperCase() + currentPath.level.slice(1)}
            </div>
            <div className="flex items-center">
              <Award className="h-5 w-5 text-yellow-500 mr-1" />
              <span className="text-sm font-medium text-gray-900">
                {currentPath.modules.reduce((total, mod) => total + (mod.status === 'completed' ? mod.xp : 0), 0)} XP
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Progreso general</span>
            <span>{currentPath.progress}%</span>
          </div>
          <div className="mt-2 h-2 bg-gray-200 rounded-full">
            <div
              className="h-2 bg-accent rounded-full transition-all duration-300"
              style={{ width: `${currentPath.progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-6">
          {currentPath.modules.map((module) => (
            <div
              key={module.id}
              className={`border rounded-lg p-4 transition-all duration-300 ${
                module.status === 'locked'
                  ? 'bg-gray-50 opacity-75'
                  : 'hover:border-accent hover:shadow-sm cursor-pointer'
              }`}
              onClick={() => module.status !== 'locked' && setSelectedModule(module)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {getModuleIcon(module.type)}
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">{module.title}</h3>
                    <p className="mt-1 text-sm text-gray-600">{module.description}</p>
                  </div>
                </div>
                {getStatusIcon(module.status)}
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500">{module.duration}</span>
                  <span className="text-sm text-gray-500">+{module.xp} XP</span>
                </div>
                {module.status !== 'locked' && (
                  <button className="text-accent hover:text-accent/90 text-sm font-medium flex items-center">
                    {module.status === 'completed'
                      ? 'Repasar'
                      : module.status === 'in-progress'
                      ? 'Continuar'
                      : 'Comenzar'}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Module Viewer Modal */}
      {selectedModule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">{selectedModule.title}</h3>
                <button
                  onClick={() => setSelectedModule(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Cerrar</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {selectedModule.type === 'video' && selectedModule.videoUrl && (
                <div className="aspect-w-16 aspect-h-9">
                  <ReactPlayer
                    url={selectedModule.videoUrl}
                    width="100%"
                    height="100%"
                    controls
                  />
                </div>
              )}

              {selectedModule.type === 'interactive' && selectedModule.content && (
                <div className="prose max-w-none">
                  {selectedModule.content}
                </div>
              )}

              {selectedModule.type === 'challenge' && (
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Complete este desafío práctico para demostrar sus habilidades.
                  </p>
                  {/* Challenge content would go here */}
                </div>
              )}

              {selectedModule.type === 'assessment' && (
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Demuestre su comprensión completando esta evaluación.
                  </p>
                  {/* Assessment content would go here */}
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-accent hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                >
                  Marcar como completado
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AILearningPath;